import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { FOUR_SECONDS, TEN_MINUTES } from '@/constants/time';
import { getInquiryV2QueryOptions, type GetInquiryV2Response } from '@/services/getInquiryV2';
import type { ClinicalCase } from '@/types/clinicalCase';
import { ApiError, fetchClient } from '@/util/fetchClient';

/**
 * The case for the viewer, or null when the backend has none to show them. A 404 means the
 * inquiry is gone or `clinical_cases_read` is off for this viewer, so it is an answer, not a
 * failure: it resolves to null and is never retried.
 */
export async function getClinicalCase(conversationId: string): Promise<ClinicalCase | null> {
  try {
    const { data } = await fetchClient.GET('/v2/inquiries/{conversationId}/clinical-case', {
      params: { path: { conversationId } },
    });

    return data?.data?.clinicalCase ?? null;
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      return null;
    }

    throw error;
  }
}

/**
 * Poll every four seconds while the case is still `preparing`, and stop on any other status or
 * once the poll has run for ten minutes, the same limit the backend uses for `preparing`.
 * Background tabs do not poll (React Query's default).
 */
export function pickClinicalCasePollInterval(
  clinicalCase: ClinicalCase | null | undefined,
  startedAt: number,
  now: number
): number | false {
  if (clinicalCase?.status !== 'preparing') {
    return false;
  }

  return now - startedAt < TEN_MINUTES ? FOUR_SECONDS : false;
}

export function getClinicalCaseQueryOptions(conversationId: string) {
  return queryOptions({
    queryKey: ['clinicalCase', conversationId],
    queryFn: () => getClinicalCase(conversationId),
  });
}

/**
 * The case to show for an open inquiry. It comes with the inquiry, so a written case needs no
 * request at all. Only while it reads `preparing` does this poll the small case endpoint, and
 * each answer is written into the cached inquiry rather than invalidating it, so no other query
 * refetches. A poll that finds no case to show (null) hides the box.
 */
export function useClinicalCase(
  conversationId: string,
  initialCase: ClinicalCase | undefined
): ClinicalCase | undefined {
  const queryClient = useQueryClient();

  const startedAt = useRef(Date.now());

  const isPreparing = initialCase?.status === 'preparing';

  useEffect(() => {
    startedAt.current = Date.now();
  }, [conversationId]);

  const { data: polled } = useQuery({
    ...getClinicalCaseQueryOptions(conversationId),
    enabled: isPreparing,
    initialData: isPreparing ? initialCase : undefined,
    initialDataUpdatedAt: 0,
    refetchInterval: (query) =>
      pickClinicalCasePollInterval(query.state.data, startedAt.current, Date.now()),
  });

  useEffect(() => {
    if (polled === undefined || polled?.status === 'preparing') {
      return;
    }

    queryClient.setQueryData<GetInquiryV2Response>(
      getInquiryV2QueryOptions({ conversationId }).queryKey,
      (inquiry) => (inquiry ? { ...inquiry, clinicalCase: polled ?? undefined } : inquiry)
    );
  }, [polled, conversationId, queryClient]);

  if (!initialCase) {
    return undefined;
  }

  if (!isPreparing) {
    return initialCase;
  }

  return polled === null ? undefined : (polled ?? initialCase);
}
